import { compact, get, keys, mergeWith, omit, pick, union, uniq } from 'lodash';

import { Locale } from '@/i18n/locales';
import {
  ClientSafeConfig,
  LocalizableContent,
  PartialTenantConfig,
  TenantConfig,
} from '@/lib/types/tenant';

const ASSISTANT_NAME_KEY = 'chat.assistantName';
const ASSISTANT_NAME_PLACEHOLDER = /\{assistantName\}/g;

export function getLocalizedValue<T>(
  config: TenantConfig | ClientSafeConfig,
  currentLocale: Locale,
  keyPath: string,
): T | null {
  const { meta, translations } = config;
  const { language: defaultLocale, availableLocales = [defaultLocale] } = meta;

  const fallbackChain = uniq(compact([currentLocale, defaultLocale, ...availableLocales]));

  for (const locale of fallbackChain) {
    const value = get(translations[locale], keyPath);
    if (value !== undefined) {
      return interpolateAssistantName({ value, config, currentLocale, keyPath }) as T;
    }
  }

  return null;
}

/**
 * Substitutes `{assistantName}` in any localized string with the tenant's
 * `chat.assistantName`, also resolved through the same locale fallback chain.
 * Lets every tenant text field (meta.title, sidebarTitle, tooltipText,
 * welcome*, etc.) reference the brand once via the placeholder instead of
 * repeating the literal string per locale.
 */
function interpolateAssistantName({
  value,
  config,
  currentLocale,
  keyPath,
}: {
  value: unknown;
  config: TenantConfig | ClientSafeConfig;
  currentLocale: Locale;
  keyPath: string;
}): unknown {
  if (typeof value !== 'string') return value;
  // Recursion guard: resolving `chat.assistantName` itself must not loop back.
  if (keyPath === ASSISTANT_NAME_KEY) return value;
  return substituteAssistantName({ value, config, currentLocale });
}

/** For text that doesn't come from `getLocalizedValue`, which already does this. */
export function substituteAssistantName({
  value,
  config,
  currentLocale,
}: {
  value: string;
  config: TenantConfig | ClientSafeConfig;
  currentLocale: Locale;
}): string {
  if (!value.includes('{assistantName}')) return value;
  const assistantName = getLocalizedValue<string>(config, currentLocale, ASSISTANT_NAME_KEY);
  if (!assistantName) return value;
  return value.replace(ASSISTANT_NAME_PLACEHOLDER, assistantName);
}

/**
 * The typed config copy, read from config through `getLocalizedValue` and never as next-intl
 * messages. `widget` also names a `common.json` section, so a namespace check alone would let the
 * launcher copy into every page's messages.
 */
const CONFIG_COPY_SECTIONS = keys({
  meta: true,
  navigation: true,
  chat: true,
  widget: true,
} satisfies Record<keyof LocalizableContent, true>);

type Messages = Record<string, unknown>;

const replaceArrays = (_target: unknown, source: unknown) =>
  Array.isArray(source) ? source : undefined;

/**
 * Deep-merges message layers like lodash `merge`, except that a later layer's list replaces the
 * earlier one whole: merged index by index, a shorter translated list would keep the English
 * list's trailing items.
 */
export function mergeMessages(...layers: Messages[]): Messages {
  return mergeWith({}, ...layers, replaceArrays);
}

interface MergeLocaleOverridesParams {
  /** The English messages every locale falls back to. */
  base: Record<string, unknown>;
  /** The locale's own messages, without the English fallback. Ignored for `en`. */
  localized?: Record<string, unknown>;
  overrides: Record<string, unknown> | undefined;
  locale: string;
}

/**
 * Layers a tenant's config-level translation overrides into its messages: English messages, the
 * tenant's English overrides, the locale's own messages, then the tenant's overrides for the
 * locale, so a tenant rewording English never replaces another locale's own string. Only overrides
 * for namespaces the messages load are applied. Every message path calls this so the order can't
 * drift apart.
 */
export function mergeLocaleOverrides({
  base,
  localized = {},
  overrides,
  locale,
}: MergeLocaleOverridesParams): Record<string, unknown> {
  const namespaces = union(keys(base), keys(localized));
  const overridesFor = (overrideLocale: string) =>
    pick(omit(overrides?.[overrideLocale] ?? {}, CONFIG_COPY_SECTIONS), namespaces);
  if (locale === 'en') return mergeMessages(base, overridesFor('en'));
  return mergeMessages(base, overridesFor('en'), localized, overridesFor(locale));
}

export function getLocalesFromConfig(config: PartialTenantConfig): string[] {
  const { translations } = config;
  if (!translations) return [];
  return Object.keys(translations).filter(key => translations[key as keyof typeof translations]);
}

interface PickTranslationLocalesParams {
  translations: TenantConfig['translations'] | undefined;
  locales: Array<string | undefined>;
}

export function pickTranslationLocales({
  translations,
  locales,
}: PickTranslationLocalesParams): TenantConfig['translations'] {
  return pick(translations ?? {}, uniq(compact(locales))) as TenantConfig['translations'];
}
